import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PAGE_SIZES } from "@/hooks/use-url-pagination";
import { formatNumber } from "@/lib/format";
import { pageWindow } from "@/lib/pagination";
import { cn } from "@/lib/utils";

interface TablePaginationProps {
  /** 1 dan boshlanadi — manzildagi `page` bilan bir xil. */
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
}

/** Sahifalash: hajm, "1–20 / 145" va raqamli tugmalar (faol sahifa suzib o'tadi). */
export function TablePagination({
  page,
  totalPages,
  total,
  limit,
  onPage,
  onLimit,
}: TablePaginationProps) {
  const { t } = useTranslation();
  const pages = Math.max(1, totalPages);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 pt-1 text-sm">
      <div className="text-muted-foreground flex items-center gap-2">
        <span className="hidden sm:inline">{t("table.rowsPerPage")}</span>
        <Select value={String(limit)} onValueChange={value => onLimit(Number(value))}>
          <SelectTrigger size="sm" aria-label={t("table.rowsPerPage")} className="w-20 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent side="top">
            {PAGE_SIZES.map(size => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="tabular-nums">
          {t("table.range", { from: formatNumber(from), to: formatNumber(to) })}{" "}
          <span className="text-foreground font-medium">
            {t("table.of", { total: formatNumber(total) })}
          </span>
        </span>
      </div>

      <nav aria-label={t("table.pages")} className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t("common.prev")}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft />
        </Button>
        {pageWindow(page, pages).map((item, index) =>
          item === "gap" ? (
            <span key={`gap-${index}`} className="text-muted-foreground px-1">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-label={t("table.goToPage", { page: item })}
              aria-current={item === page ? "page" : undefined}
              onClick={() => onPage(item)}
              className={cn(
                "relative grid size-8 place-items-center rounded-lg text-sm tabular-nums transition-colors",
                item === page
                  ? "text-primary-foreground font-semibold"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {item === page ? (
                <motion.span
                  layoutId="table-page"
                  className="bg-primary shadow-primary/30 absolute inset-0 rounded-lg shadow-sm"
                  transition={{ type: "spring", stiffness: 420, damping: 32 }}
                />
              ) : null}
              <span className="relative">{item}</span>
            </button>
          ),
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t("common.next")}
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight />
        </Button>
      </nav>
    </div>
  );
}
